import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaTags, FaPlus, FaSearch, FaEdit, FaTrash, FaBox } from 'react-icons/fa';
import inventoryService from '../services/inventoryService';
import '../styles/InventoryManagement.css';

const InventoryManagement = ({ createMode = false }) => {
    const navigate = useNavigate();

    // Estados para datos de la API
    const [productos, setProductos] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentProducto, setCurrentProducto] = useState(null);
    const [showForm, setShowForm] = useState(createMode);
    const [formData, setFormData] = useState({
        PRODUCTO_NOMBRE: '',
        PRODUCTO_CANTIDAD: '',
        UBICACIONINVENTARIO_ID: '',
        CATEGORIAINVENTARIO_ID: ''
    });
    const [errors, setErrors] = useState({});

    // Cargar datos iniciales
    useEffect(() => {
        loadInitialData(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const loadInitialData = async (p = 1, ps = 10) => {
        setLoading(true);
        try {
            const [productosRes, ubicacionesRes, categoriasRes] = await Promise.all([
                inventoryService.products.getProducts(p, ps, searchTerm),
                inventoryService.locations.getLocations(),
                inventoryService.categories.getCategories()
            ]);

            if (productosRes.success) {
                const payload = productosRes.data;
                // payload may be legacy array or paginated object { data, total, page, pageSize, totalPages }
                if (Array.isArray(payload)) {
                    setProductos(payload || []);
                    setTotal(payload.length);
                    setTotalPages(1);
                } else if (payload && payload.data) {
                    setProductos(payload.data || []);
                    setTotal(payload.total || 0);
                    setTotalPages(payload.totalPages || Math.ceil((payload.total || 0) / (payload.pageSize || ps)));
                    setPage(payload.page || p);
                    setPageSize(payload.pageSize || ps);
                } else {
                    setProductos([]);
                    setTotal(0);
                    setTotalPages(1);
                }
            } else {
                console.error('Error al cargar productos:', productosRes.message);
            }

            if (ubicacionesRes.success) {
                setUbicaciones(ubicacionesRes.data || []);
            } else {
                console.error('Error al cargar ubicaciones:', ubicacionesRes.message);
            }

            if (categoriasRes.success) {
                setCategorias(categoriasRes.data || []);
            } else {
                console.error('Error al cargar categorías:', categoriasRes.message);
            }

        } catch (err) {
            setError('Error al cargar los datos del inventario');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };



    // Obtener nombre de ubicación
    const getUbicacionNombre = (id) => {
        const ubicacion = ubicaciones.find(u => u.UBICACIONINVENTARIO_ID === id);
        return ubicacion ? ubicacion.UBICACIONINVENTARIO_NOMBRE : 'No encontrada';
    };

    // Obtener nombre de categoría
    const getCategoriaNombre = (id) => {
        const categoria = categorias.find(c => c.CATEGORIAINVENTARIO_ID === id);
        return categoria ? categoria.CATEGORIAINVENTARIO_NOMBRE : 'No encontrada';
    };

    // When using server-side search, results come already filtered from the API
    const filteredProductos = productos;

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.PRODUCTO_NOMBRE.trim()) {
            newErrors.PRODUCTO_NOMBRE = 'El nombre del producto es requerido';
        }
        if (!formData.PRODUCTO_CANTIDAD || formData.PRODUCTO_CANTIDAD <= 0) {
            newErrors.PRODUCTO_CANTIDAD = 'La cantidad debe ser mayor a 0';
        }
        if (!formData.UBICACIONINVENTARIO_ID) {
            newErrors.UBICACIONINVENTARIO_ID = 'Debe seleccionar una ubicación';
        }
        if (!formData.CATEGORIAINVENTARIO_ID) {
            newErrors.CATEGORIAINVENTARIO_ID = 'Debe seleccionar una categoría';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        try {
            const productData = {
                PRODUCTO_NOMBRE: formData.PRODUCTO_NOMBRE,
                PRODUCTO_CANTIDAD: parseInt(formData.PRODUCTO_CANTIDAD),
                CATEGORIAINVENTARIO_ID: formData.CATEGORIAINVENTARIO_ID,
                UBICACIONINVENTARIO_ID: formData.UBICACIONINVENTARIO_ID
            };

            let result;
            if (currentProducto) {
                // Actualizar producto existente
                result = await inventoryService.products.updateProduct(
                    currentProducto.PRODUCTO_ID, 
                    productData
                );
            } else {
                // Crear nuevo producto
                result = await inventoryService.products.createProduct(productData);
            }

            if (result.success) {
                alert(result.message);
                // Recargar la lista de productos
                await loadInitialData();
                handleCloseModal();
            } else {
                alert('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error al procesar producto:', err);
            alert('Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ 
            PRODUCTO_NOMBRE: '', 
            PRODUCTO_CANTIDAD: '', 
            UBICACIONINVENTARIO_ID: '', 
            CATEGORIAINVENTARIO_ID: '' 
        });
        setCurrentProducto(null);
        setErrors({});
    };

    const handleCloseModal = () => {
        setShowForm(false);
        resetForm();
    };

    // Manejar edición
    const handleEdit = (producto) => {
        setCurrentProducto(producto);
        setFormData({
            PRODUCTO_NOMBRE: producto.PRODUCTO_NOMBRE || '',
            PRODUCTO_CANTIDAD: producto.PRODUCTO_CANTIDAD || '',
            UBICACIONINVENTARIO_ID: producto.UBICACIONINVENTARIO_ID || '',
            CATEGORIAINVENTARIO_ID: producto.CATEGORIAINVENTARIO_ID || ''
        });
        setShowForm(true);
        setErrors({});
    };

    // Trigger search: reset to first page when search term changes
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            loadInitialData(1, pageSize);
        }, 350);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // Manejar eliminación
    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar este producto del inventario?')) {
            setLoading(true);
            try {
                const result = await inventoryService.products.deleteProduct(id);
                if (result.success) {
                    alert(result.message);
                    // Recargar la lista de productos
                    await loadInitialData();
                } else {
                    alert('Error: ' + result.message);
                }
            } catch (err) {
                console.error('Error al eliminar producto:', err);
                alert('Error al eliminar el producto');
            } finally {
                setLoading(false);
            }
        }
    };

    // Navegación
    const handleNavigateToLocation = () => {
        navigate('/inventory/location');
    };

    const handleNavigateToCategory = () => {
        navigate('/inventory/category');
    };

    if (loading) {
        return (
            <div className="inventory-management-container">
                <div className="inventory-management-header">
                    <h1>Gestión de Inventario</h1>
                    <p>Cargando datos del inventario...</p>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    Cargando...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inventory-management-container">
                <div className="inventory-management-header">
                    <h1>Gestión de Inventario</h1>
                    <p style={{ color: 'red' }}>Error: {error}</p>
                </div>
                <button onClick={loadInitialData}>Reintentar</button>
            </div>
        );
    }

    return (
        <div className="inventory-management-container">
            <div className="inventory-management-header">
                <h1>Gestión de Inventario</h1>
                <p>Administre los productos del inventario médico de FEPAST</p>
            </div>

            <div className="inventory-management-actions">
                <div className="search-section">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar producto, categoría o ubicación..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="action-buttons">
                    <button className="register-btn" onClick={handleNavigateToLocation}>
                        <FaMapMarkerAlt /> Gestionar Ubicaciones
                    </button>
                    <button className="register-btn" onClick={handleNavigateToCategory}>
                        <FaTags /> Gestionar Categorías
                    </button>
                    <button className="register-btn" onClick={() => setShowForm(true)}>
                        <FaPlus /> Nuevo Producto
                    </button>
                </div>
            </div>

            <div className="inventory-table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Producto</th>
                            <th>Cantidad</th>
                            <th>Ubicación</th>
                            <th>Categoría</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProductos.map(producto => (
                            <tr key={producto.PRODUCTO_ID}>
                                <td><span className="product-id">{producto.PRODUCTO_ID}</span></td>
                                <td>
                                    <div className="product-name">
                                        <div className="product-icon">
                                            <FaBox />
                                        </div>
                                        {producto.PRODUCTO_NOMBRE}
                                    </div>
                                </td>
                                <td>
                                    <span className="quantity-badge">{producto.PRODUCTO_CANTIDAD}</span>
                                </td>
                                <td>
                                    <div className="location-info">
                                        <FaMapMarkerAlt className="location-icon" />
                                        <span>{getUbicacionNombre(producto.UBICACIONINVENTARIO_ID)}</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="category-info">
                                        <FaTags className="category-icon" />
                                        <span>{getCategoriaNombre(producto.CATEGORIAINVENTARIO_ID)}</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="actions">
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={() => handleEdit(producto)}
                                            title="Editar producto"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleDelete(producto.PRODUCTO_ID)}
                                            title="Eliminar producto"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredProductos.length === 0 && (
                    <div className="table-footer">
                        No se encontraron productos en el inventario
                    </div>
                )}
            </div>

                <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                        Mostrando <strong>{filteredProductos.length}</strong> de <strong>{total}</strong> productos
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
                        <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
                        <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
                        <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>Por página:</span>
                            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </label>
                    </div>
                </div>
            
            {showForm && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FaBox />
                                {currentProducto ? 'Editar Producto' : 'Nuevo Producto'}
                            </h3>
                            <button className="modal-close" onClick={handleCloseModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="inventory-form">
                                <div className="form-section">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="PRODUCTO_NOMBRE">Nombre del Producto</label>
                                            <input
                                                type="text"
                                                id="PRODUCTO_NOMBRE"
                                                name="PRODUCTO_NOMBRE"
                                                value={formData.PRODUCTO_NOMBRE}
                                                onChange={handleChange}
                                                placeholder="Ej: Paracetamol 500mg"
                                                className={errors.PRODUCTO_NOMBRE ? 'error' : ''}
                                                disabled={currentProducto}
                                            />
                                            {errors.PRODUCTO_NOMBRE && <span className="error-message">{errors.PRODUCTO_NOMBRE}</span>}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="PRODUCTO_CANTIDAD">Cantidad</label>
                                            <input
                                                type="number"
                                                id="PRODUCTO_CANTIDAD"
                                                name="PRODUCTO_CANTIDAD"
                                                value={formData.PRODUCTO_CANTIDAD}
                                                onChange={handleChange}
                                                placeholder="0"
                                                min="1"
                                                className={errors.PRODUCTO_CANTIDAD ? 'error' : ''}
                                            />
                                            {errors.PRODUCTO_CANTIDAD && <span className="error-message">{errors.PRODUCTO_CANTIDAD}</span>}
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="UBICACIONINVENTARIO_ID">Ubicación</label>
                                            <select
                                                id="UBICACIONINVENTARIO_ID"
                                                name="UBICACIONINVENTARIO_ID"
                                                value={formData.UBICACIONINVENTARIO_ID}
                                                onChange={handleChange}
                                                className={errors.UBICACIONINVENTARIO_ID ? 'error' : ''}
                                                disabled={currentProducto}
                                            >
                                                <option value="">Seleccionar ubicación...</option>
                                                {ubicaciones.filter(ubicacion => ubicacion.UBICACIONINVENTARIO_ID !== 'LOC-000').map(ubicacion => (
                                                    <option key={ubicacion.UBICACIONINVENTARIO_ID} value={ubicacion.UBICACIONINVENTARIO_ID}>
                                                        {ubicacion.UBICACIONINVENTARIO_ID} - {ubicacion.UBICACIONINVENTARIO_NOMBRE}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.UBICACIONINVENTARIO_ID && <span className="error-message">{errors.UBICACIONINVENTARIO_ID}</span>}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="CATEGORIAINVENTARIO_ID">Categoría</label>
                                            <select
                                                id="CATEGORIAINVENTARIO_ID"
                                                name="CATEGORIAINVENTARIO_ID"
                                                value={formData.CATEGORIAINVENTARIO_ID}
                                                onChange={handleChange}
                                                className={errors.CATEGORIAINVENTARIO_ID ? 'error' : ''}
                                                disabled={currentProducto}
                                            >
                                                <option value="">Seleccionar categoría...</option>
                                                {categorias.filter(categoria => categoria.CATEGORIAINVENTARIO_ID !== 'CAT-000').map(categoria => (
                                                    <option key={categoria.CATEGORIAINVENTARIO_ID} value={categoria.CATEGORIAINVENTARIO_ID}>
                                                        {categoria.CATEGORIAINVENTARIO_ID} - {categoria.CATEGORIAINVENTARIO_NOMBRE}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.CATEGORIAINVENTARIO_ID && <span className="error-message">{errors.CATEGORIAINVENTARIO_ID}</span>}
                                        </div>
                                    </div>

                                    {currentProducto && (
                                        <div className="edit-note">
                                            <p><strong>Nota:</strong> En modo edición solo se puede modificar la cantidad del producto.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="form-actions">
                                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {currentProducto ? 'Actualizar' : 'Crear'} Producto
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryManagement;